import React, { useState, useEffect, useCallback } from 'react';
import './Grid.css';
import { SHAPES } from './Shapes';

const Grid = ({ onShapePlaced }) => {
  const [grid, setGrid] = useState(Array.from({ length: 9 }, () => Array(9).fill(null)));
  const [highlightedCells, setHighlightedCells] = useState([]);
  const [previewValid, setPreviewValid] = useState(null); // null | true | false
  const [score, setScore] = useState(0);
  const [currentPiece, setCurrentPiece] = useState(null); // { type, rotation }

  // ===== Stable helpers =====
  const canPlaceShape = useCallback((gridState, shapeType, rotation, baseX, baseY) => {
    const shape = SHAPES[shapeType][rotation];
    return shape.every(([dx, dy]) => {
      const x = baseX + dx;
      const y = baseY + dy;
      return x >= 0 && x < 9 && y >= 0 && y < 9 && gridState[x][y] === null;
    });
  }, []);

  const placeShapeOnGrid = useCallback((gridState, shapeType, rotation, baseX, baseY) => {
    const newGrid = gridState.map(row => [...row]);
    const shape = SHAPES[shapeType][rotation];
    shape.forEach(([dx, dy]) => {
      const x = baseX + dx;
      const y = baseY + dy;
      if (x >= 0 && x < 9 && y >= 0 && y < 9) newGrid[x][y] = shapeType;
    });
    return newGrid;
  }, []);

  const clearLines = useCallback((gridState) => {
    let cleared = false;
    let newGrid = gridState.map(row => [...row]);

    const rowsToClear = [];
    const colsToClear = new Set();
    const blocksToClear = [];

    newGrid.forEach((row, r) => {
      if (row.every(cell => cell !== null)) rowsToClear.push(r);
    });

    for (let c = 0; c < 9; c++) {
      if (newGrid.every(row => row[c] !== null)) colsToClear.add(c);
    }

    for (let br = 0; br < 9; br += 3) {
      for (let bc = 0; bc < 9; bc += 3) {
        let full = true;
        for (let i = 0; i < 3 && full; i++) {
          for (let j = 0; j < 3; j++) {
            if (newGrid[br + i][bc + j] === null) { full = false; break; }
          }
        }
        if (full) blocksToClear.push([br, bc]);
      }
    }

    rowsToClear.forEach(r => { newGrid[r] = Array(9).fill(null); cleared = true; });
    colsToClear.forEach(c => { for (let r = 0; r < 9; r++) newGrid[r][c] = null; cleared = true; });
    blocksToClear.forEach(([br, bc]) => {
      for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) newGrid[br + i][bc + j] = null;
      cleared = true;
    });

    if (cleared) setScore(s => s + 100);
    return newGrid;
  }, []);

  // ===== Listen for current piece broadcast from ShapePicker =====
  useEffect(() => {
    // pick up any piece set before Grid mounted
    if (window._currentPiece) setCurrentPiece(window._currentPiece);

    const onPieceChanged = (e) => {
      const { type, rotation } = e.detail || {};
      setCurrentPiece(type && rotation ? { type, rotation } : null);
    };
    window.addEventListener('piece:changed', onPieceChanged);
    return () => window.removeEventListener('piece:changed', onPieceChanged);
  }, []);

  // ===== Game Over: when the CURRENT piece cannot fit anywhere on the grid =====
  useEffect(() => {
    if (!currentPiece) return;
    const { type, rotation } = currentPiece;
    let canFit = false;
    outer: for (let x = 0; x < 9; x++) {
      for (let y = 0; y < 9; y++) {
        if (canPlaceShape(grid, type, rotation, x, y)) { canFit = true; break outer; }
      }
    }
    if (!canFit) {
      alert('Game Over!');
      setGrid(Array.from({ length: 9 }, () => Array(9).fill(null)));
      setScore(0);
      setHighlightedCells([]);
      setPreviewValid(null);
    }
  }, [grid, currentPiece, canPlaceShape]);

  // ===== Desktop preview =====
  function updatePreview(e, rowIndex, colIndex) {
    e.preventDefault();                 // keep dragover firing
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; // nudge browsers

    const shapeType = e.dataTransfer?.getData("shapeType");
    const rotation = parseInt(e.dataTransfer?.getData("rotation"), 10);
    if (!shapeType || isNaN(rotation)) {
      setHighlightedCells([]);
      setPreviewValid(null);
      return;
    }

    const shapeConfiguration = SHAPES[shapeType][rotation];
    const minX = Math.min(...shapeConfiguration.map(([dx]) => dx));
    const minY = Math.min(...shapeConfiguration.map(([_, dy]) => dy));

    const grabbedDx = parseInt(e.dataTransfer.getData("grabDx"), 10);
    const grabbedDy = parseInt(e.dataTransfer.getData("grabDy"), 10);
    const anchorDx = Number.isFinite(grabbedDx) ? grabbedDx : minX;
    const anchorDy = Number.isFinite(grabbedDy) ? grabbedDy : minY;

    const baseX = rowIndex - anchorDx;
    const baseY = colIndex - anchorDy;

    const highlighted = shapeConfiguration.map(([dx, dy]) => [baseX + dx, baseY + dy]);
    setHighlightedCells(highlighted);

    const valid = canPlaceShape(grid, shapeType, rotation, baseX, baseY);
    setPreviewValid(valid);
  }

  const handleDrop = (e, rowIndex, colIndex) => {
    e.preventDefault();

    const shapeType = e.dataTransfer?.getData("shapeType");
    const rotation = parseInt(e.dataTransfer?.getData("rotation"), 10);
    if (!shapeType || isNaN(rotation)) return;

    const shapeConfiguration = SHAPES[shapeType][rotation];
    const minX = Math.min(...shapeConfiguration.map(([dx]) => dx));
    const minY = Math.min(...shapeConfiguration.map(([_, dy]) => dy));

    const grabbedDx = parseInt(e.dataTransfer.getData("grabDx"), 10);
    const grabbedDy = parseInt(e.dataTransfer.getData("grabDy"), 10);
    const anchorDx = Number.isFinite(grabbedDx) ? grabbedDx : minX;
    const anchorDy = Number.isFinite(grabbedDy) ? grabbedDy : minY;

    const baseX = rowIndex - anchorDx;
    const baseY = colIndex - anchorDy;

    if (canPlaceShape(grid, shapeType, rotation, baseX, baseY)) {
      const updated = placeShapeOnGrid(grid, shapeType, rotation, baseX, baseY);
      const newGrid = clearLines(updated);
      setGrid(newGrid);
      if (onShapePlaced) onShapePlaced();
    }

    setHighlightedCells([]);
    setPreviewValid(null);
  };

  // ===== Mobile touch fallback hooks (unchanged) =====
  useEffect(() => {
    function mobileUpdatePreview(rowIndex, colIndex, payload) {
      const { shapeType, rotation, grabDx, grabDy } = payload || {};
      if (!shapeType || rotation == null) { setHighlightedCells([]); setPreviewValid(null); return; }

      const shapeConfiguration = SHAPES[shapeType][rotation];
      const minX = Math.min(...shapeConfiguration.map(([dx]) => dx));
      const minY = Math.min(...shapeConfiguration.map(([_, dy]) => dy));
      const anchorDx = Number.isFinite(grabDx) ? grabDx : minX;
      const anchorDy = Number.isFinite(grabDy) ? grabDy : minY;

      const baseX = rowIndex - anchorDx;
      const baseY = colIndex - anchorDy;

      const highlighted = shapeConfiguration.map(([dx, dy]) => [baseX + dx, baseY + dy]);
      setHighlightedCells(highlighted);

      const valid = canPlaceShape(grid, shapeType, rotation, baseX, baseY);
      setPreviewValid(valid);
    }

    function mobileDrop(rowIndex, colIndex, payload) {
      const { shapeType, rotation, grabDx, grabDy } = payload || {};
      if (!shapeType || rotation == null) { setHighlightedCells([]); setPreviewValid(null); return; }

      const shapeConfiguration = SHAPES[shapeType][rotation];
      const minX = Math.min(...shapeConfiguration.map(([dx]) => dx));
      const minY = Math.min(...shapeConfiguration.map(([_, dy]) => dy));
      const anchorDx = Number.isFinite(grabDx) ? grabDx : minX;
      const anchorDy = Number.isFinite(grabDy) ? grabDy : minY;

      const baseX = rowIndex - anchorDx;
      const baseY = colIndex - anchorDy;

      if (canPlaceShape(grid, shapeType, rotation, baseX, baseY)) {
        const updated = placeShapeOnGrid(grid, shapeType, rotation, baseX, baseY);
        const newGrid = clearLines(updated);
        setGrid(newGrid);
        if (onShapePlaced) onShapePlaced();
      }
      setHighlightedCells([]);
      setPreviewValid(null);
    }

    window._gridMobileUpdatePreview = mobileUpdatePreview;
    window._gridMobileDrop = mobileDrop;
    return () => {
      delete window._gridMobileUpdatePreview;
      delete window._gridMobileDrop;
    };
  }, [grid, onShapePlaced, canPlaceShape, placeShapeOnGrid, clearLines]);

  // ===== render =====
  return (
    <div className="grid">
      {grid.map((row, rowIndex) => row.map((cell, colIndex) => {
        const isHighlighted = highlightedCells.some(([x, y]) => x === rowIndex && y === colIndex);
        const classes = [
          'grid-cell',
          isHighlighted ? 'highlighted' : '',
          isHighlighted && previewValid === true ? 'preview-ok' : '',
          isHighlighted && previewValid === false ? 'preview-bad' : '',
          cell ? 'filled' : ''
        ].filter(Boolean).join(' ');

        return (
          <div
            key={`${rowIndex}-${colIndex}`}
            data-row={rowIndex}
            data-col={colIndex}
            onDragEnter={(e) => updatePreview(e, rowIndex, colIndex)}
            onDragOver={(e) => updatePreview(e, rowIndex, colIndex)}
            onDragLeave={() => { setHighlightedCells([]); setPreviewValid(null); }}
            onDrop={(e) => handleDrop(e, rowIndex, colIndex)}
            className={classes}
          >
            {cell}
          </div>
        );
      }))}
      <div>Score: {score}</div>
    </div>
  );
};

export default Grid;
